import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, ChevronRight, DoorOpen, Info, MapPin } from 'lucide-react';
import type { PublicClinicInfo, PublicClinicSummary } from '@rx-manager/shared';
import { intlLocale, useI18n } from '../../lib/i18n';
import { Card, Chip, FOCUS_RING, IconTile, cn } from '../../components/ui';

/** `2026-11-07` as "Saturday, November 7", on the clinic's own calendar. */
function dayLabel(date: string, locale: string): string {
  // Noon UTC: the same calendar day in every Canadian zone.
  return new Intl.DateTimeFormat(intlLocale(locale), {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T12:00:00Z`));
}

/** `14:30` as "2:30 p.m." or "14 h 30", as the page's language writes a time. */
function clockLabel(time: string, locale: string): string {
  const [hours, minutes] = time.split(':').map(Number);
  return new Intl.DateTimeFormat(intlLocale(locale), {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(2000, 0, 1, hours ?? 0, minutes ?? 0)));
}

/**
 * Track CGS (CGS-D3) — the top of a clinic's own page: when it runs, what to know before coming,
 * and whether it takes walk-ins. The band above already names the clinic, its address and phone.
 */
export function ClinicIntro({ clinic }: { clinic: PublicClinicInfo }) {
  const { t, locale } = useI18n();
  const sessions = useMemo(
    () =>
      clinic.sessions.map((session) => ({
        date: session.date,
        label: dayLabel(session.date, locale),
        hours: session.windows
          .map((window) => `${clockLabel(window.start, locale)} – ${clockLabel(window.end, locale)}`)
          .join(', '),
        full: session.full,
      })),
    [clinic.sessions, locale],
  );

  return (
    <section aria-labelledby="clinic-dates" className="space-y-4">
      <Card className="space-y-4">
        <div className="flex items-start gap-3">
          <IconTile icon={CalendarDays} />
          <div className="min-w-0 space-y-2">
            <h2 id="clinic-dates" className="text-lg font-semibold text-heading">
              {t('clinic.runsOn')}
            </h2>
            {sessions.length === 0 ? (
              <p className="text-base text-muted-foreground">{t('clinic.noDatesLeft')}</p>
            ) : (
              <ul className="space-y-1 text-base text-foreground">
                {sessions.map((session) => (
                  <li key={session.date} className="flex flex-wrap items-center gap-x-2">
                    <span className="font-medium text-heading">{session.label}</span>
                    <span className="text-muted-foreground">{session.hours}</span>
                    {session.full && <Chip tone="warning">{t('clinic.dayFull')}</Chip>}
                  </li>
                ))}
              </ul>
            )}
            {clinic.walkIns && (
              <p className="flex items-center gap-2 text-base font-medium text-primary-strong">
                <DoorOpen className="h-4 w-4 shrink-0" aria-hidden="true" />
                {t('clinic.walkIns')}
              </p>
            )}
          </div>
        </div>
        {clinic.instructions && (
          <div className="flex items-start gap-3 border-t border-border pt-4">
            <IconTile icon={Info} tone="muted" />
            <div className="min-w-0 space-y-1">
              <h3 className="text-base font-semibold text-heading">{t('clinic.beforeYouCome')}</h3>
              <p className="whitespace-pre-line text-base text-foreground">{clinic.instructions}</p>
            </div>
          </div>
        )}
      </Card>
      {clinic.full && (
        <p role="status" className="rounded-xl border border-warning/50 bg-warning/15 p-4 text-base font-medium text-heading">
          {t('clinic.full')}
        </p>
      )}
    </section>
  );
}

/**
 * Track CGS (CGS-D2) — the clinics the pharmacy lists now, on its main page: each leads to its own
 * page, which books only there.
 */
export function ClinicsList({
  clinics,
  hrefFor,
}: {
  clinics: PublicClinicSummary[];
  hrefFor: (code: string) => string;
}) {
  const { t, locale } = useI18n();
  if (clinics.length === 0) return null;
  return (
    <section aria-labelledby="clinics-heading" className="space-y-3">
      <h2 id="clinics-heading" className="text-lg font-semibold text-heading">
        {t('clinic.listHeading')}
      </h2>
      <ul className="space-y-3">
        {clinics.map((clinic) => (
          <li key={clinic.code}>
            <Link
              to={hrefFor(clinic.code)}
              className={cn(
                'group flex w-full items-start gap-4 rounded-xl border border-border bg-card p-4 text-left shadow-sm',
                'transition-[border-color,background-color] hover:border-primary/60 hover:bg-accent/40',
                FOCUS_RING,
              )}
            >
              <IconTile icon={MapPin} />
              <span className="min-w-0 flex-1 self-center">
                <span className="block text-base font-semibold text-heading">{clinic.name}</span>
                {clinic.address && (
                  <span className="mt-0.5 block text-sm text-muted-foreground">{clinic.address}</span>
                )}
                {clinic.nextDate && (
                  <span className="mt-0.5 block text-sm text-muted-foreground">
                    {t('clinic.next', { date: dayLabel(clinic.nextDate, locale) })}
                  </span>
                )}
              </span>
              <ChevronRight
                className="mt-3 h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
